import { DeleteOutlined, EditOutlined, PlusOutlined, UserAddOutlined, UserSwitchOutlined } from '@ant-design/icons';
import { Alert, App as AntApp, Button, Card, Checkbox, Input, Modal, Select, Space, Tabs, Tag } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { impersonate } from '../../api/appApi';
import { toast } from '../../lib/toast';
import * as api from '../api';
import { ZenListRow } from '../components';
import { useZenBootstrap, zenKeys } from '../hooks';
import type { Department, ZenUser } from '../types';
import { PageSkeleton, StatusPill } from '../ui';

type PersonDraft = {
  id?: string;
  username: string;
  name: string;
  isAdmin: boolean;
  departmentIds: string[];
  password: string;
  active: boolean;
};

type DepartmentDraft = {
  id?: string;
  name: string;
};

type ResetDraft = {
  domain: api.AdminResetDomain;
  label: string;
  step: 'phrase' | 'password';
  phrase: string;
  password: string;
};

const adminResetDefinitions: Array<{
  domain: api.AdminResetDomain;
  label: string;
  description: string;
}> = [
  {
    domain: 'work-orders',
    label: 'Work orders',
    description: 'Deletes all work orders, tasks, files, comments, billing records, linked history, and retired order records. Work-order numbering restarts at 1001.',
  },
  {
    domain: 'clients',
    label: 'Clients',
    description: 'Deletes all clients and customer-ledger entries. Work orders must be reset first.',
  },
  {
    domain: 'users',
    label: 'Non-admin users',
    description: 'Deletes every non-admin account and its area memberships. All Administrator accounts are preserved. Work orders must be reset first.',
  },
  {
    domain: 'departments',
    label: 'Custom departments',
    description: 'Deletes custom departments and their memberships while preserving fixed system areas. Work orders must be reset first.',
  },
  {
    domain: 'activity',
    label: 'Activity history',
    description: 'Clears operational and retired audit history. A new record noting this reset is retained.',
  },
];

function newPerson(): PersonDraft {
  return { username: '', name: '', isAdmin: false, departmentIds: [], password: '', active: true };
}

function newDepartment(): DepartmentDraft {
  return { name: '' };
}

export function AdminPage() {
  const { data } = useZenBootstrap();
  const { modal } = AntApp.useApp();
  const queryClient = useQueryClient();
  const [personDraft, setPersonDraft] = useState<PersonDraft | null>(null);
  const [departmentDraft, setDepartmentDraft] = useState<DepartmentDraft | null>(null);
  const [resetDraft, setResetDraft] = useState<ResetDraft | null>(null);
  const [impersonatingUserId, setImpersonatingUserId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  if (!data) return <PageSkeleton />;

  const refresh = () => queryClient.invalidateQueries({ queryKey: zenKeys.bootstrap });
  const assignableAreas = data.departments.filter((department) => department.active);
  const unmappedUsers = data.users.filter((item) => !item.isAdmin && item.departmentIds.length === 0);
  const fixedDepartmentNames = new Set([
    'sales',
    'accounts',
    'design',
    'delivery',
    'installation',
    'transportation',
    'installation/logistics',
    'installation/delivery',
  ]);

  const editPerson = (item: ZenUser) => setPersonDraft({
    id: item.id,
    username: item.username,
    name: item.name,
    isAdmin: item.isAdmin,
    departmentIds: item.departmentIds,
    password: '',
    active: item.active,
  });

  const editDepartment = (department: Department) => setDepartmentDraft({
    id: department.id,
    name: department.name,
  });

  const impersonatePerson = (item: ZenUser) => modal.confirm({
    title: `Sign in as ${item.name}?`,
    className: 'zen-responsive-confirm',
    content: `You will enter the portal as @${item.username}. Sign out and log back in as an administrator when you are finished.`,
    okText: 'Impersonate',
    onOk: async () => {
      setImpersonatingUserId(item.id);
      try {
        const success = await impersonate(item.id);
        if (!success) throw new Error('Could not impersonate this user');
        await queryClient.cancelQueries();
        queryClient.clear();
        window.location.assign('/');
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Could not impersonate user');
        setImpersonatingUserId(null);
        throw error;
      }
    },
  });

  const removePerson = (item: ZenUser) => modal.confirm({
    title: `Delete ${item.name}?`,
    className: 'zen-responsive-confirm',
    content: 'Their login will be removed. Historical work orders and audit names will be retained.',
    okText: 'Delete person',
    okButtonProps: { danger: true },
    onOk: async () => {
      try {
        await api.deleteUser(item.id);
        await refresh();
        toast.success('Person deleted');
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Could not delete person');
        throw error;
      }
    },
  });

  const removeDepartment = (department: Department) => modal.confirm({
    title: `Delete ${department.name}?`,
    className: 'zen-responsive-confirm',
    content: 'This removes the department from worker memberships. Departments with unfinished work cannot be deleted.',
    okText: 'Delete department',
    okButtonProps: { danger: true },
    onOk: async () => {
      try {
        await api.deleteDepartment(department.id);
        await refresh();
        toast.success('Department deleted');
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Could not delete department');
        throw error;
      }
    },
  });

  const beginReset = (domain: api.AdminResetDomain, label: string) => {
    setResetDraft({ domain, label, step: 'phrase', phrase: '', password: '' });
  };

  const executeReset = async () => {
    if (!resetDraft) return;
    setResetting(true);
    try {
      await api.resetAdminData(resetDraft.domain, {
        confirmation: resetDraft.phrase,
        password: resetDraft.password,
      });
      setResetDraft(null);
      await queryClient.invalidateQueries({ queryKey: ['zen'] });
      toast.success(`${resetDraft.label} reset complete`);
    } catch (error) {
      setResetDraft((current) => current ? { ...current, password: '' } : current);
      toast.error(error instanceof Error ? error.message : `Could not reset ${resetDraft.label.toLowerCase()}`);
    } finally {
      setResetting(false);
    }
  };

  return (
    <>
      <Tabs
        items={[
          {
            key: 'departments',
            label: 'Departments',
            children: (
              <Card
                title="Departments"
                extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setDepartmentDraft(newDepartment())}>Add department</Button>}
              >
                <div className="zen-list" role="list">
                  {data.departments.map((department) => {
                    const fixed = fixedDepartmentNames.has(department.name.toLowerCase());
                    const description = ['sales', 'accounts'].includes(department.name.toLowerCase())
                      ? 'Fixed platform area'
                      : fixed
                        ? 'Fixed automatic department'
                        : 'Production department';
                    return (
                      <ZenListRow
                        key={department.id}
                        title={department.name}
                        description={description}
                        actions={fixed
                          ? <Tag>Fixed</Tag>
                          : (
                            <Space size={8} wrap>
                              <Button size="small" icon={<EditOutlined />} onClick={() => editDepartment(department)}>Rename</Button>
                              <Button size="small" icon={<DeleteOutlined />} onClick={() => removeDepartment(department)}>Delete</Button>
                            </Space>
                          )}
                      />
                    );
                  })}
                </div>
              </Card>
            ),
          },
          {
            key: 'users',
            label: 'Users',
            children: (
              <Card
                title="Users"
                extra={<Button type="primary" icon={<UserAddOutlined />} onClick={() => setPersonDraft(newPerson())}>Add user</Button>}
              >
                <Space orientation="vertical" size={16} style={{ width: '100%' }}>
                  <Alert
                    type="info"
                    showIcon
                    title="Assign each person to any combination of Sales, Accounts, and work departments."
                    description="Administrator access is a separate security privilege. Only Administrators can manage people and departments."
                  />
                  {unmappedUsers.length > 0 && (
                    <Alert
                      type="warning"
                      showIcon
                      title={`${unmappedUsers.length} people need an assignment`}
                      description={unmappedUsers.map((item) => item.name).join(', ')}
                    />
                  )}
                  <div className="zen-list" role="list">
                    {data.users.map((item) => {
                      const assignments = [...(item.isAdmin ? ['Administrator'] : []), ...item.departmentNames];
                      return (
                        <ZenListRow
                          key={item.id}
                          title={item.name}
                          description={`@${item.username}${assignments.length ? ` · ${assignments.join(', ')}` : ' · No assignments'}`}
                          actions={(
                            <Space size={8} wrap>
                              <StatusPill tone={item.active ? 'success' : 'neutral'}>{item.active ? 'Active' : 'Inactive'}</StatusPill>
                              <Button
                                size="small"
                                icon={<UserSwitchOutlined />}
                                loading={impersonatingUserId === item.id}
                                disabled={
                                  !item.active
                                  || item.id === data.currentUser.id
                                  || impersonatingUserId !== null
                                }
                                onClick={() => impersonatePerson(item)}
                              >
                                Impersonate
                              </Button>
                              <Button size="small" icon={<EditOutlined />} onClick={() => editPerson(item)}>Edit</Button>
                              <Button
                                size="small"
                                icon={<DeleteOutlined />}
                                disabled={item.id === data.currentUser.id}
                                onClick={() => removePerson(item)}
                              >
                                Delete
                              </Button>
                            </Space>
                          )}
                        />
                      );
                    })}
                  </div>
                </Space>
              </Card>
            ),
          },
          {
            key: 'data-reset',
            label: 'Data reset',
            children: (
              <Card title="Data reset">
                <Space orientation="vertical" size={16} style={{ width: '100%' }}>
                  <Alert
                    type="error"
                    showIcon
                    title="These actions permanently delete data"
                    description="Every reset requires the exact word DELETE followed by the currently signed-in Administrator's password. Deleted data cannot be recovered from this application."
                  />
                  <div className="zen-list" role="list">
                    {adminResetDefinitions.map((definition) => (
                      <ZenListRow
                        key={definition.domain}
                        title={definition.label}
                        description={definition.description}
                        actions={(
                          <Button
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => beginReset(definition.domain, definition.label)}
                          >
                            Reset
                          </Button>
                        )}
                      />
                    ))}
                  </div>
                </Space>
              </Card>
            ),
          },
        ]}
      />

      <Modal
        className="zen-responsive-modal"
        open={resetDraft?.step === 'phrase'}
        title={`Reset ${resetDraft?.label ?? 'data'}?`}
        okText="Continue"
        okButtonProps={{ danger: true, disabled: resetDraft?.phrase !== 'DELETE' }}
        onCancel={() => setResetDraft(null)}
        onOk={() => setResetDraft((current) => current ? { ...current, step: 'password' } : current)}
      >
        <Space orientation="vertical" size={16} style={{ width: '100%' }}>
          <Alert
            type="error"
            showIcon
            title="This deletion is permanent"
            description={adminResetDefinitions.find((item) => item.domain === resetDraft?.domain)?.description}
          />
          <div className="form-group">
            <label>Type DELETE to continue</label>
            <Input
              autoFocus
              value={resetDraft?.phrase ?? ''}
              onChange={(event) => setResetDraft((current) => current ? { ...current, phrase: event.target.value } : current)}
              placeholder="DELETE"
              autoComplete="off"
            />
          </div>
        </Space>
      </Modal>

      <Modal
        className="zen-responsive-modal"
        open={resetDraft?.step === 'password'}
        title="Confirm that it is you"
        okText={`Reset ${resetDraft?.label ?? 'data'}`}
        okButtonProps={{ danger: true, disabled: !resetDraft?.password }}
        confirmLoading={resetting}
        closable={!resetting}
        mask={{ closable: !resetting }}
        onCancel={() => !resetting && setResetDraft(null)}
        onOk={executeReset}
      >
        <Space orientation="vertical" size={16} style={{ width: '100%' }}>
          <Alert
            type="warning"
            showIcon
            title={`Final confirmation for ${resetDraft?.label ?? 'this reset'}`}
            description={`Signed in as ${data.currentUser.name} (@${data.currentUser.username}). Enter your current password to authorize this permanent reset.`}
          />
          <div className="form-group">
            <label>Current Administrator password</label>
            <Input.Password
              autoFocus
              value={resetDraft?.password ?? ''}
              onChange={(event) => setResetDraft((current) => current ? { ...current, password: event.target.value } : current)}
              onPressEnter={() => {
                if (resetDraft?.password && !resetting) void executeReset();
              }}
              autoComplete="current-password"
            />
          </div>
        </Space>
      </Modal>

      <Modal
        className="zen-responsive-modal"
        open={Boolean(personDraft)}
        title={personDraft?.id ? 'Edit person' : 'Add person'}
        okText={personDraft?.id ? 'Save changes' : 'Add person'}
        confirmLoading={saving}
        onCancel={() => setPersonDraft(null)}
        onOk={async () => {
          if (!personDraft) return;
          setSaving(true);
          try {
            await api.saveUser(personDraft);
            await refresh();
            toast.success(personDraft.id ? 'Person updated' : 'Person added');
            setPersonDraft(null);
          } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Could not save person');
          } finally {
            setSaving(false);
          }
        }}
      >
        {personDraft && (
          <Space orientation="vertical" size={16} style={{ width: '100%' }}>
            <Alert type="info" showIcon title="A person may have any combination of assignments." description="Sales opens order creation, Accounts opens billing, and work departments open their shared queues." />
            <div className="form-grid">
              <div className="form-group">
                <label>Full name</label>
                <Input value={personDraft.name} onChange={(event) => setPersonDraft({ ...personDraft, name: event.target.value })} />
              </div>
              <div className="form-group">
                <label>Username</label>
                <Input value={personDraft.username} onChange={(event) => setPersonDraft({ ...personDraft, username: event.target.value })} />
              </div>
              <div className="form-group">
                <label>{personDraft.id ? 'New password (optional)' : 'Password'}</label>
                <Input.Password value={personDraft.password} onChange={(event) => setPersonDraft({ ...personDraft, password: event.target.value })} />
              </div>
              <div className="form-group full">
                <label>Assigned areas</label>
                <Select
                  className="assigned-areas-select"
                  mode="multiple"
                  value={personDraft.departmentIds}
                  onChange={(departmentIds) => setPersonDraft({ ...personDraft, departmentIds })}
                  placeholder="Sales, Accounts, or work departments"
                  options={assignableAreas.map((department) => ({ value: department.id, label: department.name }))}
                  style={{ width: '100%' }}
                />
              </div>
              <div className="form-group full">
                <Checkbox checked={personDraft.isAdmin} onChange={(event) => setPersonDraft({ ...personDraft, isAdmin: event.target.checked })}>Administrator access</Checkbox>
              </div>
              <div className="form-group full">
                <Checkbox checked={personDraft.active} onChange={(event) => setPersonDraft({ ...personDraft, active: event.target.checked })}>Active login</Checkbox>
              </div>
            </div>
          </Space>
        )}
      </Modal>

      <Modal
        className="zen-responsive-modal"
        open={Boolean(departmentDraft)}
        title={departmentDraft?.id ? 'Edit department' : 'Add department'}
        okText={departmentDraft?.id ? 'Save changes' : 'Add department'}
        confirmLoading={saving}
        onCancel={() => setDepartmentDraft(null)}
        onOk={async () => {
          if (!departmentDraft) return;
          setSaving(true);
          try {
            if (departmentDraft.id) await api.updateDepartment(departmentDraft.id, departmentDraft);
            else await api.createDepartment(departmentDraft);
            await refresh();
            toast.success(departmentDraft.id ? 'Department updated' : 'Department added');
            setDepartmentDraft(null);
          } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Could not save department');
          } finally {
            setSaving(false);
          }
        }}
      >
        {departmentDraft && (
          <div className="form-grid">
            <div className="form-group full">
              <label>Department name</label>
              <Input value={departmentDraft.name} onChange={(event) => setDepartmentDraft({ ...departmentDraft, name: event.target.value })} />
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
